import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Badge,
    Box,
    Button,
    ClickAwayListener,
    Divider,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Popper,
    Typography
} from '@mui/material';
import { IconBell } from '@tabler/icons';
import Transitions from 'ui-component/extended/Transitions';
import MainCard from 'ui-component/cards/MainCard';
import useAuth from 'hooks/useAuth';
import notificacionServices from 'services/notificaciones/notificacionServices';

/** Arma deep link con filtros si url_app viene sin query (notificaciones antiguas). */
const resolveNotificationUrl = (row) => {
    const base = String(row?.url_app || '').trim();
    if (base.includes('?')) {
        return base;
    }
    const id = Number(row?.id_documento);
    const tipo = String(row?.tipo_documento || '')
        .trim()
        .toUpperCase();
    if (!id) {
        return base;
    }
    switch (tipo) {
        case 'VENTA':
            return `/user/list-ventas-view?id_ventas=${id}&openDetail=1`;
        case 'NOTA_CREDITO':
            return `/user/list-nota-credito?id_nota_credito=${id}`;
        case 'RETENCION_COMPRA':
        case 'LIQUIDACION_COMPRA':
            // id_documento es retención/liquidación; sin id_compras en url hace falta el cron actualizado
            return base || '/user/list-compra-view';
        case 'GUIA_REMISION':
            return base || '/user/list-ventas-view';
        default:
            return base;
    }
};

const FarproNotificationBell = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { company, user } = useAuth();
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [noLeidas, setNoLeidas] = useState(0);

    const idUsers = Array.isArray(user) && user[0] ? user[0].id_users : user && user.id_users;
    const idCompany = company && company.id_company;

    const loadInbox = () => {
        if (!idCompany || !idUsers) {
            return;
        }
        notificacionServices
            .getInbox(idCompany, idUsers)
            .then((response) => {
                const rows = response && Array.isArray(response.data) ? response.data : [];
                const unread = rows.filter((row) => !(row.leida === true || row.leida === 't' || row.leida === 1 || row.leida === '1'));
                setItems(unread);
                setNoLeidas(response && response.noLeidas ? response.noLeidas : unread.length);
            })
            .catch(() => {
                setItems([]);
                setNoLeidas(0);
            });
    };

    useEffect(() => {
        loadInbox();
        const timer = setInterval(loadInbox, 60000);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idCompany, idUsers]);

    const handleToggle = () => {
        setOpen((prev) => !prev);
        if (!open) {
            loadInbox();
        }
    };

    const handleItem = (row) => {
        notificacionServices.marcarLeida(row.id_notificacion, idCompany, idUsers).finally(() => {
            loadInbox();
            const target = resolveNotificationUrl(row);
            if (target) {
                navigate(target);
            }
            setOpen(false);
        });
    };

    const handleMarcarTodas = () => {
        notificacionServices.marcarTodasLeidas(idCompany, idUsers).finally(() => {
            loadInbox();
        });
    };

    return (
        <Box sx={{ mr: 1 }}>
            <Badge badgeContent={noLeidas} color="error" overlap="circular">
                <Avatar
                    variant="rounded"
                    ref={anchorRef}
                    onClick={handleToggle}
                    sx={{
                        ...theme.typography.commonAvatar,
                        ...theme.typography.mediumAvatar,
                        cursor: 'pointer',
                        background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light,
                        color: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.secondary.dark,
                        '&:hover': {
                            background: theme.palette.mode === 'dark' ? theme.palette.secondary.dark : theme.palette.secondary.dark,
                            color: theme.palette.secondary.light
                        }
                    }}
                >
                    <IconBell stroke={1.5} size="20px" />
                </Avatar>
            </Badge>
            <Popper
                placement="bottom-end"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [{ name: 'offset', options: { offset: [0, 9] } }]
                }}
            >
                {({ TransitionProps }) => (
                    <Transitions in={open} {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={() => setOpen(false)}>
                                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                    <Box sx={{ p: 2, width: 360 }}>
                                        <Typography variant="h4">Notificaciones</Typography>
                                        {noLeidas > 0 && (
                                            <Button size="small" onClick={handleMarcarTodas} sx={{ mt: 1 }}>
                                                Marcar todas como leídas
                                            </Button>
                                        )}
                                    </Box>
                                    <Divider />
                                    <List sx={{ maxHeight: 360, overflow: 'auto' }}>
                                        {items.length === 0 && (
                                            <Box sx={{ p: 2 }}>
                                                <Typography variant="body2">No hay notificaciones sin leer</Typography>
                                            </Box>
                                        )}
                                        {items.map((row) => (
                                            <ListItemButton
                                                key={row.id_notificacion}
                                                onClick={() => handleItem(row)}
                                            >
                                                <ListItemText
                                                    primary={row.titulo}
                                                    secondary={row.mensaje}
                                                    primaryTypographyProps={{ variant: 'subtitle2' }}
                                                    secondaryTypographyProps={{ variant: 'caption' }}
                                                />
                                            </ListItemButton>
                                        ))}
                                    </List>
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </Box>
    );
};

export default FarproNotificationBell;
